import axios from 'axios';
import {each, keyBy, pick} from 'lodash';
import {takeEvery, call, put, delay, select} from 'redux-saga/effects';
import {assignDatasetToProject, datasetActions, getHeaders} from '.';
import {toast} from '../../base-components/StudioToast';
import URL from '../../config/url';
import {
  METADATA_BBOX,
  METADATA_IMAGE,
  METADATA_LABEL,
  METADATA_VIDEO,
} from '../../types/dataService/DatasetMetadata';
import {datasetJobActions, DATASET_JOB} from './job';

// const STAT_INFO = 'statistics/common_info'; //not used
const STAT_TYPE = 'statistics/type_aliases';
const STAT_CATEGORY_CLASSIFICATION = 'statistics/labels_by_splits';
const STAT_CATEGORY_DETECTION = 'statistics/bounding_boxes_by_splits';

const STAT_ORPHANS = 'statistics/orphans';

const POLL_INTERVAL = 2000;

const RETRY_LIMIT = 3;
const {update: updateJob, cancel: cancelJob, remove: removeJob} = datasetJobActions;
const {updateLocal: updateDataset, updateStats: updateDatasetStats} = datasetActions;

function getMetadataAnalyzeResults(data) {
  const obj = {};
  if (data.length) {
    each(keyBy(data, 'type'), (el, key) => {
      const {distribution} = el;
      switch (key) {
        case STAT_TYPE:
          obj.distribution = {
            images: distribution[METADATA_IMAGE] || 0,
            videos: distribution[METADATA_VIDEO] || 0,
            labels: distribution[METADATA_LABEL] || distribution[METADATA_BBOX] || 0,
          };
          obj.metadataDistribution = distribution;
          break;
        case STAT_CATEGORY_CLASSIFICATION:
        case STAT_CATEGORY_DETECTION:
          obj.statistics = {
            ...obj.statistics,
            category: distribution,
          };
          break;
        case STAT_ORPHANS:
          obj.statistics = {
            ...obj.statistics,
            orphans: pick(el, ['annotations', 'binaries']),
          };
          break;
        default:
          break;
      }
    });
  }
  return obj;
}

async function* pollJobStatus({payload: params}) {
  let retryTimes = 0;
  while (retryTimes < RETRY_LIMIT) {
    try {
      const {data} = yield axios.get(URL.DSS_DATASET_JOB, {
        headers: await getHeaders(),
        params,
      });
      yield put(updateJob(data));
      if (data && data.body.finished) {
        // stop polling since its finished
        put(cancelJob(params.jobId));
        return;
      }
      yield delay(POLL_INTERVAL);
    } catch (e) {
      // HTTP Errors
      retryTimes++;
      // TODO: decide if we need to put an error status for current job
    }
  }
  put(cancelJob(params.jobId));
}

function* checkJob(res) {
  const {payload: job} = res;
  if (job && job.jobId) {
    yield call(pollJobStatus, {
      payload: {datasetId: res.meta.arg.datasetId, jobId: job.jobId},
    });
  } else {
    switch (res.meta.arg.type) {
      case 'APPLY_WIP':
        if (res.meta.arg.projectId) {
          // Closing the Dataset
          // Assign the dataset to Project if it is present
          const {id, version} = res.payload.body;
          yield put(
            assignDatasetToProject({
              externalId: id,
              externalVersion: version,
              projectId: res.meta.arg.projectId,
            })
          );
        }
        break;
      // Returns Dataset aggregations that are in WIP state
      // case 'MERGE':
      // Return nothing but logs.
      // break;
      // case 'VIDEO_SAMPLING':
      // break;
      case 'ANALYZE_WIP':
        const stat = getMetadataAnalyzeResults(res.payload.body);
        // update the Dataset with WIP data
        yield put(updateDatasetStats(stat));
        yield put(updateDataset({metadataDistribution: stat.metadataDistribution}));
        break;
      default:
        return;
    }
  }
}

function* checkJobs(res) {
  // Check existing jobs that being polled or add ones that are not
  const jobsInProgress = yield select(state => state.datasetJob.inProgress);
  const list = res.payload;
  for (let i = 0; i < list.length; i++) {
    const job = list[i];
    if (!jobsInProgress.includes(job.id) && !job.finished) {
      yield call(pollJobStatus, {
        payload: {datasetId: res.meta.arg, jobId: job.id},
      });
    }
  }
}

function* deleteJob(res) {
  yield put(removeJob({jobId: res.meta.arg.jobId}));
}

function* errorJob(res) {
  yield toast.error({subtitle: res.error.message});
}

export function* watchDatasetJob() {
  yield takeEvery(`${DATASET_JOB}/get`, pollJobStatus);
  yield takeEvery(`${DATASET_JOB}/run/fulfilled`, checkJob);
  yield takeEvery(`${DATASET_JOB}/list/fulfilled`, checkJobs);
  yield takeEvery(`${DATASET_JOB}/run/rejected`, errorJob);
  yield takeEvery(`${DATASET_JOB}/finalize/fulfilled`, deleteJob);
}
